import { DateTime } from "luxon";
import type { TagType } from "@/components/Tag/Tag";
import type { RosterActivityEvent, RosterMovementRow } from "./types";
import { getMovementBand, isRosterMovementKind, type RosterMovementKind } from "./movementBands";

const STATUS_BY_KIND: Record<RosterMovementKind, { label: string; tagType: TagType }> = {
  JOINED_SECTION: { label: "Joined", tagType: "success" },
  LEFT_SECTION: { label: "Left", tagType: "warn" },
  RETIRED: { label: "Retired", tagType: "warn" },
  DECEASED: { label: "In Memoriam", tagType: "surface" }
};

export function initialsFromPersonName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function movementSubtitle(event: RosterActivityEvent): string {
  const detail = event.detail?.trim();
  if (detail) {
    return detail;
  }

  return event.headline.trim();
}

export function formatMovementDateLabel(occurredAt: string, reference: DateTime = DateTime.now()): string {
  const occurred = DateTime.fromISO(occurredAt);
  if (!occurred.isValid) {
    return "";
  }

  if (occurred.hasSame(reference, "year")) {
    return occurred.toFormat("MMM d");
  }

  return occurred.toFormat("MMM d, yyyy");
}

export function mapRosterEventToMovementRow(event: RosterActivityEvent): RosterMovementRow | null {
  if (!isRosterMovementKind(event.kind)) {
    return null;
  }

  const band = getMovementBand(event.kind);
  const status = STATUS_BY_KIND[event.kind];

  return {
    id: event.id,
    initials: initialsFromPersonName(event.personName),
    name: event.personName,
    subtitle: movementSubtitle(event),
    statusLabel: status.label,
    statusTagType: status.tagType,
    occurredAt: event.occurredAt,
    pgaId: event.pgaId,
    linkPath: event.linkPath,
    band,
    sourceEvent: event
  };
}

export function mapRosterEventsToMovementRows(events: RosterActivityEvent[]): RosterMovementRow[] {
  return events.flatMap((event) => {
    const row = mapRosterEventToMovementRow(event);
    return row ? [row] : [];
  });
}
